import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { useMutation } from '@tanstack/react-query';
import { Button, Input, Divider } from '@vnetwork/ui/primitives';
import { Field } from '@vnetwork/ui/forms';
import { Alert, useToast } from '@vnetwork/ui/feedback';
import { verifyMigrationOtp, sendMigrationOtp } from '@/api/auth-flow';

interface Props {
  email: string;
  /** Called with the one-time migration token once the OTP is verified. */
  onVerified: (migrationToken: string) => void;
}

interface FormValues {
  otp: string;
}

const RESEND_SECONDS = 60;

export function MigrationOtpForm({ email, onVerified }: Props) {
  const navigate = useNavigate();
  const toast = useToast();
  const [error, setError] = useState<string | null>(null);
  const [cooldown, setCooldown] = useState(RESEND_SECONDS);

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<FormValues>({ defaultValues: { otp: '' } });

  const verify = useMutation({ mutationFn: verifyMigrationOtp });
  const resend = useMutation({ mutationFn: sendMigrationOtp });

  useEffect(() => {
    if (cooldown <= 0) return;
    const id = setInterval(() => setCooldown((c) => c - 1), 1000);
    return () => clearInterval(id);
  }, [cooldown]);

  const onSubmit = async (values: FormValues) => {
    setError(null);
    try {
      const result = await verify.mutateAsync({ email, otp: values.otp });
      if (result?.migration_token) {
        onVerified(result.migration_token);
        return;
      }
      setError('Verification failed. Please try again.');
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Invalid or expired verification code.');
    }
  };

  const handleResend = async () => {
    if (cooldown > 0) return;
    setError(null);
    try {
      await resend.mutateAsync(email);
      setCooldown(RESEND_SECONDS);
      toast.success("We've sent a new verification code to your email.");
    } catch {
      setError('Could not resend the code. Please try again shortly.');
    }
  };

  return (
    <form onSubmit={handleSubmit(onSubmit)} style={{ display: 'grid', gap: 'var(--vn-space-4)' }}>
      {error ? <Alert tone="danger" title={error} /> : null}

      <Field
        label="Verification code"
        required
        description={`Enter the 6-digit code sent to ${email}.`}
        error={errors.otp?.message}
      >
        {(p) => (
          <Input
            {...p}
            inputMode="numeric"
            placeholder="000000"
            autoComplete="one-time-code"
            invalid={!!errors.otp}
            {...register('otp', {
              required: 'Code is required.',
              pattern: { value: /^\d{6}$/, message: 'Enter the 6-digit code.' },
            })}
          />
        )}
      </Field>

      <div style={{ textAlign: 'right', fontSize: 'var(--vn-font-size-md)' }}>
        {cooldown > 0 ? (
          <span style={{ color: 'var(--vn-fg-muted)' }}>Resend in {cooldown}s</span>
        ) : (
          <button
            type="button"
            onClick={handleResend}
            disabled={resend.isPending}
            style={{
              background: 'none',
              border: 0,
              cursor: 'pointer',
              color: 'var(--vn-primary-solid)',
              textDecoration: 'underline',
            }}
          >
            Resend code
          </button>
        )}
      </div>

      <Button type="submit" variant="primary" fullWidth loading={verify.isPending}>
        Verify
      </Button>

      <Divider>or</Divider>

      <Button type="button" variant="secondary" fullWidth onClick={() => navigate('/auth/login')}>
        Use another account
      </Button>
    </form>
  );
}
